import React, { useEffect, useRef, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import * as braze from '@braze/web-sdk'
import { Map } from 'immutable'
import { isLocal, isTest } from '../../environment/index'
import { get as getConfig } from '../../config/index'
import { isBrowser } from '../../config/environment'
import { selectAuth } from '../../services/auth/selectors'

function normalizeBrazeSdkBaseUrl (endpoint: string): string {
  const trimmed = endpoint.trim()
  if (!trimmed) return ''
  try {
    if (trimmed.includes('://')) {
      return new URL(trimmed).host
    }
  } catch {
    // fall through
  }
  return trimmed.replace(/^https?:\/\//i, '')
}

const isTestEnvironment = isTest()

const config = isBrowser() ? getConfig() : null
const isBrazeFeatureOn = !!config?.features?.braze
const isBrazeIamFeatureOn = !!config?.features?.brazeIAM
const brazeApiKey = (config?.brazeWebSdkApiKey as string | undefined) || ''
const brazeEndpointRaw = (config?.brazeSdkEndpoint as string | undefined) || ''
const brazeBaseUrl = normalizeBrazeSdkBaseUrl(brazeEndpointRaw)

let brazeInitializeSucceeded = false

if (
  isBrowser()
  && isBrazeFeatureOn
  && brazeApiKey
  && brazeBaseUrl
  && !isTestEnvironment
) {
  const enableLogging = isLocal()
  brazeInitializeSucceeded = braze.initialize(brazeApiKey, {
    baseUrl: brazeBaseUrl,
    // Required for dashboard HTML in-app messages (and JS click actions). Default is false.
    allowUserSuppliedJavascript: isBrazeIamFeatureOn,
    ...(enableLogging ? { enableLogging: true } : {}),
  })
  if (brazeInitializeSucceeded && isBrazeIamFeatureOn) {
    // Must run before openSession or session-start / trigger IAMs are dropped.
    braze.automaticallyShowInAppMessages()
  }
}

/**
 * Syncs Braze user identity with Redux auth: changeUser when logged in;
 * wipeData only on transition from logged-in to anonymous (logout).
 */
const BrazeUserSync: React.FC = () => {
  const auth = useSelector(selectAuth)
  const prevUidRef = useRef<string | undefined>(undefined)
  const initialSessionOpenedRef = useRef(false)

  useEffect(() => {
    if (!brazeInitializeSucceeded) return

    const uidRaw = Map.isMap(auth) ? auth.get('uid') : undefined
    const uid = uidRaw != null && String(uidRaw).trim() !== ''
      ? String(uidRaw)
      : undefined

    const prevUid = prevUidRef.current

    if (uid) {
      if (uid !== prevUid) {
        braze.changeUser(uid)
        braze.openSession()
        prevUidRef.current = uid
      }
      initialSessionOpenedRef.current = true
      return
    }

    if (prevUid) {
      braze.wipeData()
      braze.openSession()
      prevUidRef.current = undefined
      initialSessionOpenedRef.current = true
      return
    }

    if (!initialSessionOpenedRef.current) {
      braze.openSession()
      initialSessionOpenedRef.current = true
    }
  }, [auth])

  return null
}

export interface GaiaBrazeProviderProps {
  children: ReactNode
}

/**
 * Initializes Braze Web SDK when features.braze, API key, and endpoint are set (no OneTrust gate).
 * Renders identity sync for changeUser / wipeData(on logout) and openSession ordering.
 */
export const GaiaBrazeProvider: React.FC<GaiaBrazeProviderProps> = ({ children }) => (
  <>
    {brazeInitializeSucceeded ? <BrazeUserSync /> : null}
    {children}
  </>
)

export default GaiaBrazeProvider
